import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    CardContent,
    Typography,
    Container,
    Paper,
    Fade,
} from '@mui/material';
import {
    Email as EmailIcon,
    CheckCircle as CheckCircleIcon,
    Security as SecurityIcon,
    Lock as LockIcon,
} from '@mui/icons-material';

import { FormState } from './PasswordFunctions';
import RenderEmailStep from './RenderEmailStep';
import RenderVerificationStep from './RenderVerificationStep';
import RenderPasswordResetStep from './RenderPasswordResetStep';
import RenderSuccessStep from './RenderSuccessStep';

const PasswordRecovery: React.FC = () => {
    const [formState, setFormState] = useState<FormState>({
        email: '',
        verificationCode: '',
        newPassword: '',
        confirmPassword: '',
        id_usuario: 0,
        isLoading: false,
        isSuccess: false,
        isCodeSent: false,
        isCodeVerified: false,
        isPasswordReset: false,
        error: null,
        isEmailValid: false,
        errorType: null,
        resendCooldown: 0,
        showNewPassword: false,
        showConfirmPassword: false,
    });

    const resendTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (formState.resendCooldown > 0) {
            resendTimerRef.current = setTimeout(() => {
                setFormState(prev => ({
                    ...prev,
                    resendCooldown: prev.resendCooldown - 1,
                }));
            }, 1000);
        }

        return () => {
            if (resendTimerRef.current) {
                clearTimeout(resendTimerRef.current);
            }
        };
    }, [formState.resendCooldown]);

    const getCurrentStep = () => {
        if (formState.isSuccess) return 'success';
        if (formState.isCodeVerified && !formState.isPasswordReset) return 'password-reset';
        if (formState.isCodeSent && !formState.isCodeVerified) return 'verification';
        return 'email';
    };

    const getHeaderInfo = () => {
        const step = getCurrentStep();
        switch (step) {
            case 'success':
                return {
                    icon: CheckCircleIcon,
                    title: 'Senha Redefinida!',
                    subtitle: 'Processo concluído com sucesso',
                    gradient: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                };
            case 'password-reset':
                return {
                    icon: LockIcon,
                    title: 'Nova Senha',
                    subtitle: 'Defina sua nova senha segura',
                    gradient: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                };
            case 'verification':
                return {
                    icon: SecurityIcon,
                    title: 'Verificar Email',
                    subtitle: 'Digite o código de 6 dígitos',
                    gradient: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                };
            default:
                return {
                    icon: EmailIcon,
                    title: 'Recuperar Senha',
                    subtitle: 'Digite seu email usado no cadastro',
                    gradient: 'linear-gradient(45deg, #030303 30%, #262626 90%)',
                };
        }
    };

    const headerInfo = getHeaderInfo();
    const HeaderIcon = headerInfo.icon;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2,
            }}
        >
            <Container maxWidth="sm">
                <Fade in timeout={800}>
                    <Paper
                        elevation={24}
                        sx={{
                            borderRadius: 4,
                            overflow: 'hidden',
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        <Box
                            sx={{
                                background: headerInfo.gradient,
                                color: 'white',
                                padding: 4,
                                textAlign: 'center',
                            }}
                        >
                            <HeaderIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />

                            <Typography variant="h4" component="h1" fontWeight="bold">
                                {headerInfo.title}
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
                                {headerInfo.subtitle}
                            </Typography>
                        </Box>

                        <CardContent sx={{ padding: 4 }}>
                            {getCurrentStep() === 'email' && <RenderEmailStep formState={formState} setFormState={setFormState} />}
                            {getCurrentStep() === 'verification' && <RenderVerificationStep formState={formState} setFormState={setFormState} />}
                            {getCurrentStep() === 'password-reset' && <RenderPasswordResetStep formState={formState} setFormState={setFormState} />}
                            {getCurrentStep() === 'success' && <RenderSuccessStep />}
                        </CardContent>
                    </Paper>
                </Fade>
            </Container>
        </Box>
    );
};

export default PasswordRecovery;